import React, {Component} from 'react';
import axios from 'axios';
import Auth from '../../../auth/auth';
import ShareSightDrawer from './ShareSightDrawer';

// ponytail: self-contained (own axios + local state), matching ClassificationPanel next door.
const API = process.env.REACT_APP_API_URL || '';

/**
 * Mockup screen S4's "🔒 X — who can see it" panel.
 *
 * It answers *who*, and — the part worth building — *why*. "Benefits team — Owner, sight travels
 * with ownership" is a different fact from "Sam Okonkwo — acting for Priya Raman until 1 Sep", and
 * a list of bare names answers neither.
 *
 * "Privacy & Compliance", which the mockup draws, is NOT rendered. It appears nowhere in this
 * codebase as a group or a role; the real mechanism is the global `auditor` role. Printing an
 * org-specific team name the system does not know would be the same fabrication as inventing a
 * per-row owner.
 */
class WhoCanSeePanel extends Component {
  constructor(props) {
    super(props);
    this.state = {sight: null, error: '', loading: false, shareOpen: false};
  }

  componentDidMount() {
    this.load();
  }

  // The drawer swaps `selectedItem` IN PLACE without unmounting (dd-details-view's openDrawer), so
  // React reuses this instance across objects. Without this reset, opening A then B shows A's
  // audience under B's name — a false statement about who can see something, on the panel whose
  // entire subject is who can see something. Same guard, same reason, as ClassificationPanel.
  componentDidUpdate(prev) {
    if (prev.entityId !== this.props.entityId) {
      this.setState({sight: null, error: ''}, () => this.load());
    }
  }

  load() {
    const {entityId} = this.props;
    if (!entityId) return;
    this.setState({loading: true, error: ''});
    axios.get(`${API}/access/object-sight/${entityId}`, {
      headers: {'Accept': 'application/json', 'x-access-token': new Auth().getAccessToken()},
    })
        .then((res) => this.setState({sight: res.data, loading: false}))
        .catch((err) => this.setState({loading: false,
          error: String((err.response && err.response.data && err.response.data.error) || err.message || err)}));
  }

  renderViewer(v) {
    return (
      <div className="sight-row" key={v.subjectId} data-testid="dd-sight-row">
        <span className="sight-who">{v.displayName}</span>
        {/* The expiry is rendered for ANY time-boxed reason, not delegation's alone. Slice 4d's
            shares are expiring grants too — rule 6 refuses one without an end date — and showing
            "Shared with them, expires" with no date would describe a temporary grant as permanent
            on the screen an owner reads to check who still has sight. */}
        <span className="sight-why">
          {v.delegatedFromName ?
            `acting for ${v.delegatedFromName}${v.expiresAt ? ` until ${new Date(v.expiresAt).toLocaleDateString()}` : ''}` :
            `${v.reason}${v.expiresAt ? ` ${new Date(v.expiresAt).toLocaleDateString()}` : ''}`}
        </span>
      </div>
    );
  }

  render() {
    const {sight, error, loading} = this.state;
    if (loading && !sight) return null;
    if (error) {
      return <div className="sight-error" data-testid="dd-sight-error">{error}</div>;
    }
    if (!sight) return null;

    return (
      <div className="who-can-see" data-testid="dd-sight-panel" data-entity-id={this.props.entityId}>
        {!sight.confidential ? (
          <div className="sight-row" data-testid="dd-sight-everyone">
            <span className="sight-who">Everyone with access to the schema</span>
            <span className="sight-why">This table is internal — it is not restricted</span>
          </div>
        ) : (
          <>
            {/* A guarded result must not render as a bare list. computeEffect's lesson applies
                verbatim: "nobody else can see this" is what a correctly restricted table looks
                like AND what a broken population read looks like. */}
            {!sight.measured ? (
              <div className="sight-unmeasured" data-testid="dd-sight-unmeasured">
                Not measured — {sight.guards.map((g) => g.message).join(' ')}
              </div>
            ) : (
              <>
                {!sight.viewers.length && (
                  <div className="sight-row"><span className="sight-who">Nobody beyond its owners</span></div>
                )}
                {sight.viewers.map((v) => this.renderViewer(v))}
                {sight.capped && (
                  <div className="sight-capped" data-testid="dd-sight-capped">
                    Showing {sight.capped.shown} of {sight.capped.total}
                  </div>
                )}
              </>
            )}

            <div className="sight-row sight-everyone-else">
              <span className="sight-who">Everyone else</span>
              <span className="sight-why">
                Table does not appear in lists, search, lineage, dependency graphs or reports
              </span>
            </div>
          </>
        )}

        <div className="sight-actions">
          <button type="button" className="link-button" data-testid="dd-sight-share"
            onClick={() => this.setState({shareOpen: true})}>
            + Share sight with a team
          </button>
          <span className="sight-note">
            Sharing sight grants visibility only — never stewardship. Today, sight travels with
            ownership or with a direct grant.
          </span>
        </div>

        <ShareSightDrawer
          open={this.state.shareOpen}
          onClose={() => this.setState({shareOpen: false})}
          onSuccess={() => this.load()}
          scopeType="object"
          scopeId={this.props.entityId}
        />

        {/* THE SAME HONESTY DeclassificationEffect ALREADY CARRIES. `ddFilters` returns `1=1` until
            the flip, so "does not appear in lists, search, lineage" is a promise about enforcement
            that is not switched on — stating it flatly would be asserting a behaviour that is not
            live. */}
        {!sight.enforcing && (
          <div className="sight-shadow-note" data-testid="dd-sight-shadow-note">
            Enforcement is not switched on yet, so this describes what will be hidden at the flip —
            not what is hidden today.
          </div>
        )}
      </div>
    );
  }
}

export default WhoCanSeePanel;
